/* eslint-disable @typescript-eslint/no-explicit-any */
/* eslint-disable @typescript-eslint/no-unsafe-return */
/* eslint-disable @typescript-eslint/no-unsafe-assignment */
/* eslint-disable @typescript-eslint/no-unsafe-member-access */
/* eslint-disable @typescript-eslint/no-unsafe-argument */

import { lazy } from "react";
import { matchPath } from "react-router-dom";

import {
  guardedReload,
  isChunkLoadError,
  isNewVersionDeployed,
  markUpdateError,
  reloadToRetryChunk,
  reportChunkFailure,
} from "./new-version";

type ComponentImport = () => Promise<any>;

const importComponent = async (componentImport: ComponentImport) => {
  const component = await componentImport();
  if (component?.default) {
    return component;
  }
  const named = Object.values(component).find(
    (value) => typeof value === "function",
  );
  return { default: named };
};

// Only for a page being rendered. A preload must not reload the tab under someone who is only resting on a link.
const reloadOnChunkLoadError = async (error: unknown) => {
  if (isChunkLoadError(error)) {
    // true: a deploy removed the chunk. false: the server answered with the
    // version this tab already runs, so the request just failed. Browsers remember
    // a failed module until the page reloads, so a reload is the only retry there
    // is, in both cases. undefined: out of reach, where a reload would lose the page.
    const newVersion = await isNewVersionDeployed();
    if (!newVersion) {
      reportChunkFailure(error);
    }
    const reloading = newVersion
      ? await guardedReload()
      : newVersion === false && (await reloadToRetryChunk());
    // Keep showing the loading state while it reloads. If it can't, this reaches
    // the ErrorBoundary.
    if (reloading) {
      return new Promise<never>(() => undefined);
    }
    if (newVersion) {
      markUpdateError(error);
    }
  }
  throw error;
};

export const loadWithRetry = (componentImport: ComponentImport) =>
  importComponent(componentImport).catch(reloadOnChunkLoadError);

let loadedComponents = 0;
const loadedComponentListeners = new Set<() => void>();

// For RouteSuspense. A new value means a lazy component's code has arrived.
export const getLoadedComponents = () => loadedComponents;
export const subscribeToLoadedComponents = (listener: () => void) => {
  loadedComponentListeners.add(listener);
  return () => {
    loadedComponentListeners.delete(listener);
  };
};

export const lazyWithRetry = (componentImport: ComponentImport) => {
  let loaded: unknown;
  let loading: Promise<any> | undefined;

  const loadOnce = () =>
    (loading ??= importComponent(componentImport).then(
      (module) => {
        loaded = module;
        // A timeout, because React only reads the module after this promise's own callbacks.
        setTimeout(() => {
          loadedComponents += 1;
          loadedComponentListeners.forEach((listener) => listener());
        });
        return module;
      },
      (error: unknown) => {
        // A later render or preload tries again.
        loading = undefined;
        throw error;
      },
    ));

  const Component = lazy(() =>
    // React.lazy suspends on a promise even if the code has arrived, and every fallback costs a commit and a
    // wait. A thenable that answers at once is read in the same render, so preloaded code never suspends.
    loaded
      ? ({
          then: (resolve: (module: unknown) => void) => resolve(loaded),
        } as Promise<any>)
      : loadOnce().catch(reloadOnChunkLoadError),
  );

  // `page`, for a route with nested lazy routes: its module's preloadPage loads the one `page` opens, or the default one.
  const preload = (page?: string) =>
    loadOnce().then((module) => module.preloadPage?.(page));

  return Object.assign(Component, { preload, preloadCode: loadOnce });
};

interface Preloadable {
  preload: () => Promise<unknown>;
}

// With the company prefix group users have, which the route tables below leave out.
const currentPath = () =>
  window.location.pathname.replace(/^\/company\/[^/]+(?=\/|$)/, "");

// A page sits behind several lazy steps, each found only after the one before has rendered. Called where a route's
// components are defined, with the routes in the order the page's Switch has them, this starts the step for the URL
// being opened as soon as the module has loaded. The steps then load side by side, and the page renders in one go, with
// its queries in one round. Only the step's own code: the table in its module finds the next step, and `preload` would
// also fetch a section's default page.
export function preloadRouteBeingOpened(
  routes: [string, { preloadCode: () => Promise<unknown> }][],
) {
  const pathname = currentPath();
  const route = routes.find(([path]) => matchPath(pathname, { path }));
  route?.[1].preloadCode().catch(() => undefined);
}

const IDLE_QUEUE_DELAY_MS = 3000;
const idleQueue: Preloadable[] = [];
let idleQueueRunning = false;

const whenIdle = (callback: () => void) => {
  if ("requestIdleCallback" in window) {
    window.requestIdleCallback(callback, { timeout: 3000 });
  } else {
    setTimeout(callback, 500);
  }
};

const runIdleQueue = () => {
  const next = idleQueue.shift();
  if (!next) {
    idleQueueRunning = false;
    return;
  }
  next
    .preload()
    .catch(() => undefined)
    .finally(() => whenIdle(runIdleQueue));
};

// Downloads the pages people go to next, one at a time and only while the tab has nothing else to do, so that moving
// there doesn't wait for code.
export function preloadWhenIdle(...components: Preloadable[]) {
  const connection = (
    navigator as { connection?: { saveData?: boolean; effectiveType?: string } }
  ).connection;
  if (connection?.saveData || connection?.effectiveType?.endsWith("2g")) {
    return;
  }
  idleQueue.push(...components);
  if (!idleQueueRunning) {
    idleQueueRunning = true;
    // Not while the page that was asked for is still loading.
    setTimeout(() => whenIdle(runIdleQueue), IDLE_QUEUE_DELAY_MS);
  }
}
